'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { PlayIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';


// Swiper Essential Styles
import 'swiper/css';
import 'swiper/css/navigation';
import NextImage from '@/features/Image';

interface Product {
  id: number;
  slug: string;
  name: string;
  price: number;
  sale_price: number;
   thumbnail_image: string;
}

interface ShortVideo {
  slug: string;
  banner?: string;
  title?: string;
  video?: string;
  products?: Product[]; 
}

interface ShortsSliderProps {
  shorts: ShortVideo[];
  title?: string;
}

const RelatedProductSwiper = ({ products }: { products: Product[] }) => {
  if (!products || products.length === 0) return null;

  return (
    <div className="related-product-swiper mt-3">
      <Swiper
        modules={[Pagination, Autoplay]}
        direction="vertical"
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        loop={true}
        autoplay={{ delay: 4000, disableOnInteraction: true }}
        className="inner-product-slider"
        spaceBetween={10}
        slidesPerView={1} 
        style={{ height: '94px' }}
      >
        {products.map((item) => (
          <SwiperSlide key={item.id} >
            <div className="mini-product-card bg-white">
              <div className="mini-product-thumb">
                <Link href={`/product/${item.slug}`}>
                  <img 
                    src={item.thumbnail_image} 
                    alt={item.name}
                    width={60} 
                    height={60} 
                    className="object-fit-contain"
                  />
                </Link>
              </div>
              
              <div className="mini-product-info">
                <p className="product-name">
                  <Link href={`/product/${item.slug}`}>
                    {item.name}
                  </Link>
                </p>
                
                <div className="product-price">
                  <span className="current">৳{item.sale_price}</span>
                  {item.price > item.sale_price && (
                    <span className="discount">
                      {" "} / <del>৳{item.price}</del>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const ShortCard = ({
  short,
  onHoverStart,
  onHoverEnd
}: {
  short: ShortVideo;
  onHoverStart: () => void;
  onHoverEnd: () => void;
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [iframeLoaded, setIframeLoaded] = useState<boolean>(false);
  const [isPreloaded, setIsPreloaded] = useState<boolean>(false);
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPreloaded(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // preload when 10% of the card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getCleanVideoSrc = (url: string | undefined) => {
    if (!url) return "";
    let cleanUrl = url;
    if (cleanUrl.includes("watch?v=")) {
      cleanUrl = cleanUrl.replace("watch?v=", "embed/");
    }
    const videoId = cleanUrl.split('/').pop()?.split('?')[0];
    const connector = cleanUrl.includes("?") ? "&" : "?";
    
    return `${cleanUrl}${connector}autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1`;
  };

  return (
    <article
      ref={cardRef}
      className="post post--short rounded-0"
      onMouseEnter={() => {
        setIsHovered(true);
        onHoverStart();
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        // keep iframeLoaded true so the video stays cached for future hovers
        onHoverEnd();
      }}
    >
      <figure className="post__media post__video rounded-3">
        <div className="video-viewport-lock">
          
          {/* render iframe only when preloaded to avoid loading all videos at once */}
          {isPreloaded && short.video && (
            <div className={`video-layer ${iframeLoaded ? 'is-loaded' : ''} ${isHovered ? 'is-active' : ''}`}>
              <iframe
                src={getCleanVideoSrc(short.video)}
                title={short.title || "Short Video"}
                frameBorder="0"
                scrolling="no" 
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen={true}
                onLoad={() => setIframeLoaded(true)}
              ></iframe>
            </div>
          )}

          <div className="banner-layer">
            <NextImage
              src={short?.banner}
              alt={short?.title || 'Short video'}
              fill
              className="rounded-3"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          </div>

          <div className={`btn__video btn__iframe ${iframeLoaded ? 'is-hidden' : ''}`}>
            <HugeiconsIcon icon={PlayIcon} size={26} />
          </div>
          
        </div>
      </figure>

      <div className="post__body p-0 mt-2">
        {short.products && (
           <RelatedProductSwiper products={short.products} />
        )}
      </div>
    </article>
  );
};


export const ShortsSlider: React.FC<ShortsSliderProps> = ({ shorts, title = "Shorts" }) => {
  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  if (!shorts || shorts.length === 0) return null;

  return (
    <section className="shorts-slider-wrapper padding-top padding-bottom section-bg">
      <div className="container">
        <div className="section-header d-flex justify-content-between align-items-center">
          <div className="section-header__content">
            <h2 className='fw-medium'> KireiTube </h2>
          </div>

          <div className="section-header__action">
            <Link className="trk-btn trk-btn--primary" href="/kirei-tube">
              View All
            </Link>
          </div>
        </div>

        <Swiper
          modules={[Navigation, Autoplay]}
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          navigation={{
            nextEl: '.shorts-next',
            prevEl: '.shorts-prev',
          }}
          autoplay={{ delay: 5000, disableOnInteraction: true }}
          spaceBetween={20}
          breakpoints={{
            375: { slidesPerView: 1.5 },
            576: { slidesPerView: 2.5 },
            768: { slidesPerView: 3 },
            992: { slidesPerView: 4 },
            1200: { slidesPerView: 4.5 },
            1400: { slidesPerView: 4.75 },
          }}
        >
          {shorts.map((short, index) => (
            <SwiperSlide key={index}>
              <ShortCard 
                short={short} 
                onHoverStart={() => swiperInstance?.autoplay?.stop()}
                onHoverEnd={() => swiperInstance?.autoplay?.start()}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};